import {
  Component, Input, Output, EventEmitter,
  OnChanges, OnInit, SimpleChanges,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, Subscription } from 'rxjs';
import { AuthService } from 'src/app/services/auth.service';
import { EntityService } from 'src/app/services/entity.service'
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { UsersService } from 'src/app/services/users.service';
import { EditEntityLogoComponent } from '../edit-entity-logo/edit-entity-logo.component';

@Component({
  selector: 'app-general',
  templateUrl: './general.component.html',
  styleUrls: [ './general.component.scss' ],
})
export class GeneralComponent implements OnInit, OnChanges {
  @Input() defaults: any;
  @Input() error: any;
  @Input() entity: any;
  @Output() setNewDefaults = new EventEmitter<object>();
  @Output() refreshUser = new EventEmitter<any>();

  $entity: any;

  primaryEmail = '';
  primaryContact = '';
  primaryBuildingName = '';
  localTimezone = '';
  fileTypeInS3 = 'ENTITY_LOGO';
  logoSrc: SafeUrl | null = null;
  noUploadDropzone = true;
  loggedInSubscription: Subscription;
  reloadLogoSub: Subscription;
  user$: Observable<any>;
  entity$: Observable<any>;
  private subs: Subscription = new Subscription();

  usTimezones = [
    // Eastern Time Zone
    { value: 'America/New_York', label: 'Eastern Time (ET) - New York' },
    { value: 'America/Detroit', label: 'Eastern Time (ET) - Detroit/Michigan' },
    { value: 'America/Indiana/Indianapolis', label: 'Eastern Time (ET) - Indianapolis' },
    { value: 'America/Indiana/Vincennes', label: 'Eastern Time (ET) - Vincennes' },
    { value: 'America/Indiana/Winamac', label: 'Eastern Time (ET) - Winamac' },
    { value: 'America/Indiana/Marengo', label: 'Eastern Time (ET) - Marengo' },
    { value: 'America/Indiana/Petersburg', label: 'Eastern Time (ET) - Petersburg' },
    { value: 'America/Indiana/Tell_City', label: 'Central Time (CT) - Tell City' },
    { value: 'America/Indiana/Knox', label: 'Central Time (CT) - Knox' },
    { value: 'America/Indiana/Vevay', label: 'Eastern Time (ET) - Vevay' },
    { value: 'America/Kentucky/Louisville', label: 'Eastern Time (ET) - Louisville' },
    { value: 'America/Kentucky/Monticello', label: 'Eastern Time (ET) - Monticello' },
    // Central Time Zone
    { value: 'America/Chicago', label: 'Central Time (CT) - Chicago' },
    { value: 'America/Menominee', label: 'Central Time (CT) - Menominee' },
    { value: 'America/North_Dakota/Center', label: 'Central Time (CT) - North Dakota Center' },
    { value: 'America/North_Dakota/New_Salem', label: 'Central Time (CT) - New Salem' },
    { value: 'America/North_Dakota/Beulah', label: 'Central Time (CT) - Beulah' },
    // Mountain Time Zone
    { value: 'America/Denver', label: 'Mountain Time (MT) - Denver' },
    { value: 'America/Boise', label: 'Mountain Time (MT) - Boise' },
    { value: 'America/Phoenix', label: 'Mountain Standard Time (MST) - Arizona (No DST)' },
    // Pacific Time Zone
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT) - Los Angeles' },
    // Alaska Time Zone
    { value: 'America/Anchorage', label: 'Alaska Time (AKT) - Anchorage' },
    { value: 'America/Juneau', label: 'Alaska Time (AKT) - Juneau' },
    { value: 'America/Sitka', label: 'Alaska Time (AKT) - Sitka' },
    { value: 'America/Metlakatla', label: 'Alaska Time (AKT) - Metlakatla' },
    { value: 'America/Yakutat', label: 'Alaska Time (AKT) - Yakutat' },
    { value: 'America/Nome', label: 'Alaska Time (AKT) - Nome' },
    { value: 'America/Adak', label: 'Hawaii-Aleutian Time (HAT) - Adak' },
    // Hawaii Time Zone
    { value: 'Pacific/Honolulu', label: 'Hawaii Time (HST) - Honolulu (No DST)' },
    { value: 'Etc/UTC', label: 'Coordinated Universal Time (UTC)' },
  ];

  constructor(
    private authService: AuthService, private entityService: EntityService, private sanitizer: DomSanitizer, private usersService: UsersService, public dialog: MatDialog,
  ) {
  }

  ngOnInit() {
    this.authService.entity$.subscribe((entity) => {
      this.$entity = entity;

      if (this.$entity) {
        this.primaryContact = this.$entity?.primaryContact;
        this.primaryEmail = this.$entity?.primaryEmail;
        this.primaryBuildingName = this.$entity?.Config?.PrimaryBuilding?.buildingName;
      }
    });

    this.user$ = this.authService.user$
    this.entity$ = this.authService.entity$
    this.reloadLogoSub = this.usersService.reloadLogo$.subscribe(() => this.loadEntityLogo());

    this.subs.add(this.authService.entity$.subscribe((entity) => {
      if (entity) {
        this.loadEntityLogo();
      }
    }));
  }

  ngOnChanges(changes: SimpleChanges): void {
    // When entity or defaults inputs change, re-initialize
    if (changes['entity'] || changes['defaults']) {
      this.initializeFromInputs();
    }
  }

  initializeFromInputs(): void {
    const sourceEntity = this.entity || this.defaults;

    if (sourceEntity) {
      // Account owner resolution lives in EntityService only; component just consumes it.
      const owner = this.entityService.getAccountOwner(sourceEntity);
      this.primaryContact = owner?.fullName ?? '';
      this.primaryEmail = owner?.email ?? '';
      this.primaryBuildingName = sourceEntity?.Config?.PrimaryBuilding?.buildingName ?? '';
      this.localTimezone = sourceEntity?.Config?.localTimezone ?? sourceEntity?.localTimezone ?? '';
    }
  }

  loadEntityLogo(): void {
    this.authService.user$.subscribe(user => {
      if (user && user.loginName) {
        this.entityService.getEntityLogo(user.loginName).subscribe({
          next: (response: any) => {
            if (response && response.data && response.data.logoUrl) {
              this.logoSrc = response.data.logoUrl;
            }
          },
          error: (error) => {
            console.error('Failed to load entity logo:', error);
          },
        });
      }
    }).unsubscribe();
  }

  onImageFileSelected(blobUrl: any) {
    this.logoSrc = this.sanitizer.bypassSecurityTrustUrl(blobUrl);
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  getBuildingId = (buildingName: string) => {
    const foundBuilding = this.entity?.Buildings?.find((b: any) => b.buildingName === buildingName);
    return Number(foundBuilding?.id);
  }

  updateDefaults = (event: any, key: any) => {
    let value = event.target.value;

    if (key === 'primaryBuildingId') {
      value = this.getBuildingId(value);
    }

    if (key === 'primaryContact') {
      this.primaryContact = value;

      const email = this.populateEmail(value);
      if (email) {
        this.primaryEmail = email;
        this.setNewDefaults.emit({ primaryEmail: email });
      }
    }

    if (key === 'primaryEmail') {
      this.primaryEmail = value;
    }

    const newDefaults = { [key]: value };
    this.setNewDefaults.emit(newDefaults);
  }

  populateEmail = (contactName: string): string | null => {
    if (contactName === '') return null;
    const foundUser = this.entity?.Users?.find((u: any) => u.fullName === contactName);

    if (!foundUser) return null;

    const email = foundUser?.contactEmail || foundUser.loginName;
    return email || null;
  }

  openChangeEntityLogoModal = () => {
    const dialogRef = this.dialog.open(EditEntityLogoComponent)

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        this.refreshUser.emit(result);
      }
    });
  }

}
